import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/Layout';
import API_BASE_URL, { API_BASE_URL_WITHOUT_API } from '../config/api';
import './CustomerForm.css';

const CustomerForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;
  const [loading, setLoading] = useState(isEdit);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('profile');
  const [formData, setFormData] = useState({
    name: '',
    displayName: '',
    gender: '',
    dateOfBirth: '',
    address: '',
    nationality: '',
    phone: '',
    email: '',
    isActive: true
  });
  const [profilePicture, setProfilePicture] = useState(null);
  const [currentProfilePicture, setCurrentProfilePicture] = useState(null);
  const [wishlistData, setWishlistData] = useState({ hotels: [], spas: [] });
  const [loadingWishlist, setLoadingWishlist] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isEdit) {
      fetchCustomer();
    }
  }, [id]);

  const fetchCustomer = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/admin/customers/${id}`);
      const customer = response.data.data;
      setFormData({
        name: customer.name || '',
        displayName: customer.displayName || '',
        gender: customer.gender || '',
        dateOfBirth: customer.dateOfBirth ? new Date(customer.dateOfBirth).toISOString().split('T')[0] : '',
        address: customer.address || '',
        nationality: customer.nationality || '',
        phone: customer.phone || '',
        email: customer.email || '',
        isActive: customer.isActive !== undefined ? customer.isActive : true
      });
      if (customer.profilePicture) {
        setCurrentProfilePicture(`${API_BASE_URL_WITHOUT_API}${customer.profilePicture}`);
      }
      setLoading(false);
      
      // Fetch wishlist data
      await fetchCustomerWishlist(id);
    } catch (error) {
      setError('Failed to fetch customer');
      setLoading(false);
    }
  };

  const fetchCustomerWishlist = async (customerId) => {
    try {
      setLoadingWishlist(true);
      const response = await axios.get(`${API_BASE_URL}/admin/customers/${customerId}`);
      if (response.data.success && response.data.data) {
        setWishlistData({
          hotels: response.data.data.wishlistHotels || [],
          spas: response.data.data.wishlistSpas || []
        });
      }
    } catch (error) {
      console.error('Error fetching wishlist:', error);
      setWishlistData({ hotels: [], spas: [] });
    } finally {
      setLoadingWishlist(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleProfilePictureChange = (e) => {
    setProfilePicture(e.target.files[0]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);

    try {
      const submitData = new FormData();
      Object.keys(formData).forEach(key => {
        if (formData[key] !== null && formData[key] !== undefined) {
          submitData.append(key, formData[key]);
        }
      });
      
      if (profilePicture) {
        submitData.append('profilePicture', profilePicture);
      }

      if (isEdit) {
        await axios.put(`${API_BASE_URL}/admin/customers/${id}`, submitData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      } else {
        await axios.post(`${API_BASE_URL}/admin/customers`, submitData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }
      navigate('/sy-admin/customers');
    } catch (error) {
      setError(error.response?.data?.message || `Failed to ${isEdit ? 'update' : 'create'} customer`);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="customer-form-container">
          <div className="customer-form-header">
            <div className="skeleton skeleton-text" style={{ width: '300px', height: '40px', borderRadius: '8px' }}></div>
            <div className="skeleton skeleton-button" style={{ width: '180px', height: '40px', borderRadius: '8px' }}></div>
          </div>
          <div className="customer-form-card">
            <div className="form-section">
              <div className="skeleton skeleton-text" style={{ width: '200px', height: '32px', borderRadius: '8px', marginBottom: '24px' }}></div>
              <div className="form-row">
                <div className="form-group">
                  <div className="skeleton skeleton-text" style={{ width: '100px', height: '16px', borderRadius: '4px', marginBottom: '8px' }}></div>
                  <div className="skeleton skeleton-text" style={{ width: '100%', height: '44px', borderRadius: '8px' }}></div>
                </div>
                <div className="form-group">
                  <div className="skeleton skeleton-text" style={{ width: '120px', height: '16px', borderRadius: '4px', marginBottom: '8px' }}></div>
                  <div className="skeleton skeleton-text" style={{ width: '100%', height: '44px', borderRadius: '8px' }}></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="customer-form-container">
        <div className="customer-form-header">
          <h1>{isEdit ? 'Edit Customer' : 'Add New Customer'}</h1>
          <button
            onClick={() => navigate('/sy-admin/customers')}
            className="btn btn-secondary"
          >
            ← Back to Customers
          </button>
        </div>

        {error && (
          <div className="error-message">
            <span className="error-icon">⚠️</span>
            {error}
          </div>
        )}

        <div className="customer-form-card">
          <div className="form-tabs">
            <button
              className={`tab-button ${activeTab === 'profile' ? 'active' : ''}`}
              onClick={() => setActiveTab('profile')}
            >
              Profile
            </button>
            {isEdit && (
              <button
                className={`tab-button ${activeTab === 'wishlist' ? 'active' : ''}`}
                onClick={() => setActiveTab('wishlist')}
              >
                Wishlist
              </button>
            )}
          </div>

          {activeTab === 'profile' && (
            <form onSubmit={handleSubmit}>
              <div className="form-section">
                <h2>Profile Information</h2>
                
                <div className="form-group profile-picture-group">
                  <label>Profile Picture</label>
                  <div className="profile-picture-container">
                    {currentProfilePicture && (
                      <div className="current-profile-picture">
                        <img
                          src={currentProfilePicture}
                          alt="Current profile"
                          className="profile-preview"
                        />
                      </div>
                    )}
                    {profilePicture && (
                      <div className="new-profile-picture">
                        <img
                          src={URL.createObjectURL(profilePicture)}
                          alt="New profile"
                          className="profile-preview"
                        />
                        <span className="new-picture-label">New</span>
                      </div>
                    )}
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleProfilePictureChange}
                      className="file-input"
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="name">Name</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Full name"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="displayName">Display Name</label>
                    <input
                      type="text"
                      id="displayName"
                      name="displayName"
                      value={formData.displayName}
                      onChange={handleChange}
                      placeholder="Display name"
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="phone">Phone</label>
                    <input
                      type="text"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="Phone number"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="email">Email</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="Email address"
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="gender">Gender</label>
                    <select
                      id="gender"
                      name="gender"
                      value={formData.gender}
                      onChange={handleChange}
                    >
                      <option value="">Not specified</option>
                      <option value="male">Male</option>
                      <option value="female">Female</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label htmlFor="dateOfBirth">Date of Birth</label>
                    <input
                      type="date"
                      id="dateOfBirth"
                      name="dateOfBirth"
                      value={formData.dateOfBirth}
                      onChange={handleChange}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="address">Address</label>
                  <textarea
                    id="address"
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    rows="3"
                    placeholder="Address"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="nationality">Nationality</label>
                  <input
                    type="text"
                    id="nationality"
                    name="nationality"
                    value={formData.nationality}
                    onChange={handleChange}
                    placeholder="Nationality"
                  />
                </div>

                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      name="isActive"
                      checked={formData.isActive}
                      onChange={handleChange}
                    />
                    <span>Active</span>
                  </label>
                  <small>Inactive customers cannot log in</small>
                </div>
              </div>

              <div className="form-actions">
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? 'Saving...' : (isEdit ? 'Update Customer' : 'Create Customer')}
                </button>
                <button
                  type="button"
                  onClick={() => navigate('/sy-admin/customers')}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
              </div>
            </form>
          )}

          {activeTab === 'wishlist' && isEdit && (
            <div className="wishlist-section">
              <h2>Wishlist</h2>
              
              {loadingWishlist ? (
                <div className="wishlist-loading">Loading wishlist...</div>
              ) : (
                <>
                  <div className="wishlist-category">
                    <h3>
                      <span className="wishlist-icon">🏨</span>
                      Hotels ({wishlistData.hotels.length})
                    </h3>
                    {wishlistData.hotels.length === 0 ? (
                      <p className="wishlist-empty">No wishlisted hotels</p>
                    ) : (
                      <div className="wishlist-items">
                        {wishlistData.hotels.map((hotel) => (
                          <div key={hotel._id} className="wishlist-item">
                            {hotel.featuredImage && (
                              <img
                                src={`${API_BASE_URL_WITHOUT_API}${hotel.featuredImage}`}
                                alt={hotel.name}
                                className="wishlist-item-image"
                              />
                            )}
                            <div className="wishlist-item-details">
                              <div className="wishlist-item-name">{hotel.name}</div>
                              <div className="wishlist-item-location">
                                {hotel.city}, {hotel.state}
                              </div>
                              {hotel.minPricePerNight > 0 && (
                                <div className="wishlist-item-price">
                                  {hotel.currency === 'INR' ? '₹' : hotel.currency}
                                  {hotel.minPricePerNight.toFixed(0)}/night
                                </div>
                              )}
                              <span className={`wishlist-item-status ${hotel.status}`}>
                                {hotel.status}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="wishlist-category">
                    <h3>
                      <span className="wishlist-icon">💆</span>
                      Spas ({wishlistData.spas.length})
                    </h3>
                    {wishlistData.spas.length === 0 ? (
                      <p className="wishlist-empty">No wishlisted spas</p>
                    ) : (
                      <div className="wishlist-items">
                        {wishlistData.spas.map((spa) => (
                          <div key={spa._id} className="wishlist-item">
                            {spa.featuredImage && (
                              <img
                                src={`${API_BASE_URL_WITHOUT_API}${spa.featuredImage}`}
                                alt={spa.name}
                                className="wishlist-item-image"
                              />
                            )}
                            <div className="wishlist-item-details">
                              <div className="wishlist-item-name">{spa.name}</div>
                              <div className="wishlist-item-location">
                                {spa.city}, {spa.state}
                              </div>
                              {spa.minPrice > 0 && (
                                <div className="wishlist-item-price">
                                  {spa.currency === 'INR' ? '₹' : spa.currency}
                                  {spa.minPrice.toFixed(0)}
                                  {spa.maxPrice && spa.maxPrice > spa.minPrice && ` - ${spa.maxPrice.toFixed(0)}`}
                                </div>
                              )}
                              <span className={`wishlist-item-status ${spa.status}`}>
                                {spa.status}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default CustomerForm;
